<template>
    <div class="flex justify-center">
        <div class="flex flex-col items-center justify-center gap-4" v-if="patentListIns.loading">
            <CatLoading  />
        </div>

        <div v-else-if="patentListIns.isEmpty" class="flex flex-col items-center justify-center">
            <CatHole2 class="mb-2 h-[50px] w-[70px]"/>
            <span class="text-sm text-grey-90">
                {{ t('noData') }}
            </span>
        </div>

        <div v-else class="flex w-full flex-col gap-4 pb-4">
            <div class="flex items-center justify-between gap-2">
                <div class="flex items-center gap-2">
                    <span class="flex items-center gap-1" v-html="t('totalPatents',{total: patentListIns.total})"></span>
                    <div class="th-tooltip th-tooltip-top-start" :data-tip="t('totalRecordsTip')">
                        <PtIcon icon="SolidInfo" class="fill-grey-55 text-2xl" />
                    </div>
                    <a v-if="srpLink" class="th-btn th-btn-text px-0" :href="srpLink" target="_blank">{{ t('viewAll') }}</a>
                </div>
                <div>
                    <BatchSave :result="{ solutionIds }" />
                </div>
            </div>

            <SolutionCard :withBorder="true" :order="index + 1" :solution="item" v-for="(item, index) in patentListIns.list"
                          :key="item.SOLUTION_ID">
                <template #preTitle>
                    <span>
                        {{ formatDateToLocal({ date: item.PATENT_UPDATE_TS }) }}
                    </span>
                </template>
            </SolutionCard>

            <div class="mx-auto" v-if="patentListIns.hasNext">
                <span v-if="patentListIns.moreLoading" class="th-loading th-loading-spinner th-loading-md"></span>
                <button v-else class="th-btn" @click="handleMore">{{ t('viewMore') }}</button>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { SolutionCard } from '@eureka/solution-card';
import { CatLoading, CatHole2 } from '@eureka/cat'
import { useI18n } from 'vue-i18n';
import { PtIcon } from 'patsnap-biz';
import { PatentListService } from '@/services/patent-list-service';
import { Site, SLD } from '@patsnap-utils/biz';
import { formatDateToLocal } from '@eureka/utils/date-format'
import { SOLUTION_TYPE } from '@eureka/solution-card/src/types';
import { useGlobalState } from '@/composables/use-global-store';
import { controlDeferred, EurekaProduct } from '@eureka/utils/control';
import { CommonApis } from '@eureka/services/src/apis/common.api';
import { generateSimpleQuery } from '@eureka/utils/block-to-query';
import { Playbooks } from '@eureka/services/src/types/playbook';
import { useCompanyBasic } from '@/composables/use-company-basic';
import { ESearchField } from '@/types';
import { BatchSave } from '@eureka/eureka-save';
import { hookErrorCode } from '@eureka/services/src/custom-error-handler'
import '@patsnap-ui/icon/assets/solid/info.svg'
import { LIMITED_PERMISSION_CODE } from '@/constant';

const { t } = useI18n()
const { companyId, checkedChild, updateLimitInfo } = useGlobalState()
const { companyInfo } = useCompanyBasic()

const patentListIns = ref(new PatentListService())

const oneIdQuery = ref('')
const treeIdQuery = ref('')

const userProduct = ref(EurekaProduct.GENERAL)

const basicParams = computed(() => ({
    entity_id: companyId.value,
    solution_type: SOLUTION_TYPE.PATENT,
    include_children: checkedChild.value,
}))

const solutionIds = computed(() => {
    return patentListIns.value.list.map(item => item.SOLUTION_ID)
})

const srpLink = computed(() => {
    const playbook = userProduct.value === EurekaProduct.MATERIAL ? Playbooks.MATERIAL : Playbooks.SMART
    const qid = checkedChild.value ? treeIdQuery.value : oneIdQuery.value
    if (!qid) return ''
    return `${new Site({ sld: SLD.EUREKA }).href}/results/standard?playbook=${playbook}&qid=${qid}`
})

function handleMore() {
    patentListIns.value.loadMore(basicParams.value)
}

async function generateQId(companyName:string) {
    const { product } = await controlDeferred.promise
    userProduct.value = product
    const { qid: oneId } = await CommonApis.queryItemToShort({
        q_s: generateSimpleQuery(ESearchField.ONE_ID, {
            name: companyName,
            value: companyId.value,
        }),
    });
    oneIdQuery.value = oneId;
    const { qid: treeId } = await CommonApis.queryItemToShort({
        q_s: generateSimpleQuery(ESearchField.TREE_COMPANY, {
            name: companyName,
            value: companyId.value,
        }),
    });
    treeIdQuery.value = treeId;
}

watch(() => checkedChild.value, () => {
    hookErrorCode(patentListIns.value.loadInitialData({
        ...basicParams.value,
    }), LIMITED_PERMISSION_CODE, () => {
        updateLimitInfo(true)
    })
}, {
    immediate: true,
})

/**
 * 防止basic请求还没结束，就开始请求queryItemToShort
 */
watch(() => companyInfo.value?.name, () => {
    if (!companyInfo.value?.name) return
    generateQId(companyInfo.value?.name)
}, {
    immediate: true,
})

</script>
